import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["CustomCell", "extraCellProps", "objectType", "object", "refreshCache"];
import { memo, useEffect } from 'react';
import get from 'transmute/get';
import isNumber from 'transmute/isNumber';
import memoize from 'transmute/memoize';
import count from 'transmute/count';
import Raven from 'raven-js';

// @ts-expect-error: no types
import { isError, isLoading } from 'reference-resolvers/utils';

// @ts-expect-error: no types
import ResolveReferences from 'reference-resolvers/ResolveReferences';
import { propertyLabelTranslator } from 'property-translator/propertyTranslator';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ListingLibLoadingCell from 'framework-listing-lib/table/ListingLibLoadingCell';
import ListingLibStringCell from 'framework-listing-lib/table/ListingLibStringCell';
import resolverSerializer from 'framework-listing-lib/internal/Table/resolverSerializer';
import { referencedObjectTypes } from 'framework-listing-lib/internal/Table/constants/referencedObjectTypes';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import { REFRESH_RESOLVED_CELL } from 'framework-listing-lib/constants/tableEvents';
import { SENTRY_KEYS } from 'framework-listing-lib/utils/sentry';
import ListingLibTruncateDiv from './ListingLibTruncateDiv';

// Adapted from `customer-data-table/static/js/cells/ResolvedCell.js
import { jsx as _jsx } from "react/jsx-runtime";
export const getIdArray = lookupId => {
  if (!lookupId) {
    return [];
  }
  if (isNumber(lookupId)) {
    return [`${lookupId}`];
  }

  // NOTE: see `customer-data-property-utils/parseMultiEnumValue`
  //       (customer-data-table doesn't have a dep on this library or we'd call it directly)
  return lookupId.split(';').map(id => id.trim()).filter(id => id !== '');
};
export function ResolvedCellComponent(_ref) {
  let {
      CustomCell,
      extraCellProps,
      objectType,
      object,
      refreshCache
    } = _ref,
    cellProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  useEffect(() => {
    listingLibObserver.on(REFRESH_RESOLVED_CELL, id => {
      if (!id || id === cellProps.id || Array.isArray(id) && id.includes(cellProps.id)) {
        refreshCache(cellProps.id);
      }
    });
  }, [cellProps.id, refreshCache]);
  if (!object) {
    return /*#__PURE__*/_jsx(ListingLibStringCell, Object.assign({}, cellProps, extraCellProps, {
      value: cellProps.value || cellProps.id
    }));
  }
  const firstObject = get(0, object);
  if (isLoading(firstObject)) {
    return /*#__PURE__*/_jsx(ListingLibLoadingCell, {});
  }
  if (isError(firstObject)) {
    return /*#__PURE__*/_jsx(ListingLibTruncateDiv, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "frameworkListingLib.cells.resolver.error"
      })
    });
  }
  if (count(object) > 1) {
    const value = object.map(x => get('label', x)).join(', ');
    return /*#__PURE__*/_jsx(ListingLibTruncateDiv, {
      children: /*#__PURE__*/_jsx(ListingLibStringCell, Object.assign({}, cellProps, extraCellProps, {
        value: value
      }))
    });
  }
  if (CustomCell) {
    return /*#__PURE__*/_jsx(ListingLibTruncateDiv, {
      children: /*#__PURE__*/_jsx(CustomCell, Object.assign({}, cellProps, extraCellProps, {
        objectType: objectType,
        value: resolverSerializer(firstObject, objectType)
      }))
    });
  }
  return /*#__PURE__*/_jsx(ListingLibTruncateDiv, {
    children: /*#__PURE__*/_jsx(ListingLibStringCell, Object.assign({}, cellProps, extraCellProps, {
      value: propertyLabelTranslator(get('label', firstObject))
    }))
  });
}
export const getResolver = memoize((propertyName, objectType, resolvers) => resolvers[referencedObjectTypes[propertyName]] || resolvers[referencedObjectTypes[objectType]]);
const mapResolversToProps = (resolvers, {
  id,
  value,
  property,
  objectType
}) => {
  const propertyName = get('name', property);
  const externalOptions = get('externalOptions', property);
  const resolver = getResolver(propertyName, objectType, resolvers);
  if (!resolver) {
    Raven.captureException(new Error(SENTRY_KEYS.MISSING_RESOLVER), {
      extra: {
        id,
        propertyName
      },
      fingerprint: [SENTRY_KEYS.MISSING_RESOLVER],
      tags: {
        lib: 'framework-listing-lib'
      }
    });
    return {};
  }
  const lookupId = externalOptions ? value : id;
  const lookupIds = getIdArray(lookupId);
  const values = lookupIds.map(_id => resolver.byId(_id));
  return values ? {
    object: values,
    refreshCache: resolver.refreshCache
  } : {
    refreshCache: resolver.refreshCache
  };
};
const ListingLibResolvedCell = ResolveReferences(mapResolversToProps)(ResolvedCellComponent);
export default /*#__PURE__*/memo(ListingLibResolvedCell);